import { useId, useLayoutEffect, useRef, useState } from "react";
import type { ChangeEvent, FocusEvent, InputHTMLAttributes, ReactNode, RefObject } from "react";
import { formatWholeMoney, parseWholeMoney } from "../lib/money";

// 單價輸入格。業主原話(2026-09-10):
// 「報價單內的單價 不用小數點 但要規範數字規格 0,000,000這樣」
// 業主另外確認:**單價連輸入也只能整數**,打小數會被四捨五入成整數,
// 畫面上看到的數字就是真正拿去算的數字。
//
// 四件事在這裡一起收:
//
// 1. **打字的當下就套千分位**(38000 → 38,000),不必等離開欄位才看到格式,
//    也就不會有人對著一串沒有撇號的數字數位數。
// 2. **小數點不當場吃掉。** 若在 onChange 就把值收成整數,一個字一個字打「1234.5」
//    會變成:打到「1234.」時小數點被丟掉,接著的 5 直接黏成「12345」——
//    金額當場差十倍,而且畫面上沒有任何異狀。所以小數尾巴留在畫面上,
//    **離開欄位**才四捨五入。
// 3. **往外送的一律是整數字串**(ROUND_HALF_UP,與後端 `pricing.to_whole_dollars`
//    同一條規則),即使使用者還在打小數,存進草稿的也已經是整數。
//    顯示與送出走**同一條**清理規則,不各清各的。
// 4. **游標留在原處。** 格式化輸入只要讓千分位逗號移位,React 就把整串新字串
//    寫回 DOM,瀏覽器依規範把游標歸到字尾 —— 改中間一位數之後接著連按退格,
//    會從尾巴一路吃掉正確的位數。所以每次更新後都要把游標放回去。
//
// 全程走 money.ts 的 decimal.js 版函式,不用 parseFloat / Number。

/**
 * 打字中的顯示:整數部分即時套千分位,剛打的小數尾巴先原樣留著。
 * 逗號、空白、其他字元一律先濾掉,免得「1,234」被當成第二次分組而變形。
 */
export function groupWhileTyping(raw: string): string {
  const cleaned = String(raw).replace(/[^\d.-]/g, "");
  if (cleaned === "") return "";
  const negative = cleaned.startsWith("-");
  const body = cleaned.replace(/-/g, "");
  const dot = body.indexOf(".");
  const intDigits = dot < 0 ? body : body.slice(0, dot);
  // 第二個以後的小數點是打錯的,直接丟掉
  const tail = dot < 0 ? "" : `.${body.slice(dot + 1).replace(/\./g, "")}`;
  const grouped = intDigits === "" ? "" : formatWholeMoney(intDigits);
  return `${negative ? "-" : ""}${grouped}${tail}`;
}

/**
 * 一串文字裡有幾個「不是千分位逗號」的字元。
 *
 * 游標位置一律換算成這個數再還原。**刻意不是只數數字**:2026-09-10 第四輪
 * 在正式站台實測抓到,只數數字的話,打到「1,234.」時游標會被放回第 4 個
 * 數字之後 —— 也就是小數點的左邊 —— 接著打的 5 就插在小數點前面,畫面變成
 * 「12,345.」,離開欄位後是 12,345,金額當場差十倍而且看不出異狀。
 * 小數點與負號同樣是使用者打進去的字元,一起數才追得上他的游標。
 */
function countKept(text: string): number {
  let seen = 0;
  for (const ch of text) {
    if (ch !== ",") seen += 1;
  }
  return seen;
}

/**
 * 依「游標左側有幾個非逗號字元」在新的顯示字串裡找回等價的位置。
 *
 * 不照字元索引還原,是因為千分位逗號會隨著每一次按鍵左右移動:
 * 「1,234,567」刪掉一位數之後是「134,567」,字元索引 2 在舊字串裡指向「2」,
 * 在新字串裡卻指向「4」。逗號是程式加的、其餘都是使用者打的,
 * 「左邊有幾個使用者打的字元」不受逗號影響,才是穩定的座標。
 */
export function caretAfterKept(text: string, kept: number): number {
  if (kept <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] !== ",") {
      seen += 1;
      if (seen === kept) return i + 1;
    }
  }
  return text.length;
}

/**
 * 單價欄的行為(顯示、送出、游標)只有這一份實作。
 *
 * 抽成 hook 而不是只留元件,是因為報價單明細列要的是一個裸 `<input>`
 * (安靜介面的樣式自己給),品項庫的新增對話框要的是帶標籤與說明的 `Input`。
 * 兩邊各寫一份格式化就會像 2026-09-10 審查抓到的那樣,同一件事有兩種行為。
 */
export function useWholeMoneyField(
  value: string | number | null | undefined,
  onValueChange: (next: string) => void,
  onBlur?: (event: FocusEvent<HTMLInputElement>) => void,
  onFocus?: (event: FocusEvent<HTMLInputElement>) => void,
): { ref: RefObject<HTMLInputElement>; props: InputHTMLAttributes<HTMLInputElement> } {
  // 打字中的原字串;null 代表沒在編輯,畫面直接照 value 格式化。
  // 不留這一份草稿的話,使用者打到一半的「1,234.」會被外面回傳的整數蓋掉。
  const [draft, setDraft] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);
  // 這一次按鍵之後,游標左側應該有幾個非逗號字元。
  // null 代表這次更新不是打字造成的,別動游標。
  const caretKept = useRef<number | null>(null);

  // 用 layout effect:要趕在瀏覽器畫出來之前把游標放回去,不然會看到它閃一下字尾。
  useLayoutEffect(() => {
    const el = ref.current;
    const kept = caretKept.current;
    caretKept.current = null;
    if (el == null || kept == null) return;
    // 焦點已經不在這一格時不要硬搶游標,那會把使用者從別的欄位拉回來。
    if (document.activeElement !== el) return;
    const at = caretAfterKept(el.value, kept);
    el.setSelectionRange(at, at);
  });

  return {
    ref,
    props: {
      type: "text",
      // 手機叫出數字鍵盤。單價只有整數,不需要 decimal 那顆小數點鍵。
      inputMode: "numeric",
      autoComplete: "off",
      value: draft ?? formatWholeMoney(value),
      onChange: (event: ChangeEvent<HTMLInputElement>) => {
        const raw = event.target.value;
        const caret = event.target.selectionStart;
        caretKept.current = countKept(caret == null ? raw : raw.slice(0, caret));
        // 先清理再解析,顯示與送出走同一條路。
        //
        // 兩邊各清各的會出人命:groupWhileTyping 是逐字元丟雜訊,parseWholeMoney
        // 是整串比對,整串裡只要有一個雜訊字元就回 "0"。貼上「NT$38,000」時
        // 畫面寫著 38,000、草稿裡的單價卻已經是 0,同一列的金額欄當場變 0.00 ——
        // 正是「畫面上看到的數字就是真正拿去算的數字」要擋的那件事。
        const shown = groupWhileTyping(raw);
        setDraft(shown);
        onValueChange(parseWholeMoney(shown));
      },
      onFocus: (event: FocusEvent<HTMLInputElement>) => {
        // **值是 0 的時候,進到欄位就把它整個選起來。**
        //
        // 新的一列單價預設是 "0",那個 0 是**佔位用的**,不是使用者填的數字。
        // 不選起來的話,點一下那一格游標多半落在 0 的左邊,打 1500 就變成
        // "15000" —— 金額當場差十倍,而畫面上沒有任何異狀
        // (業主 2026-09-17 在正式站上抓到)。
        //
        // **只在 0 的時候全選。** 已經有金額的那一格要維持原本的游標行為,
        // 不然想改中間一位數的人會整串被蓋掉,那比原本的問題更煩。
        const el = event.currentTarget;
        if (parseWholeMoney(el.value) === "0") {
          el.select();
        }
        onFocus?.(event);
      },
      onBlur: (event: FocusEvent<HTMLInputElement>) => {
        // 離開欄位:草稿丟掉,畫面回到正規化後的整數千分位
        setDraft(null);
        onBlur?.(event);
      },
    },
  };
}

export interface MoneyInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type" | "inputMode"> {
  /** 目前的單價,整數字串(例 "38000")。 */
  value: string | number | null | undefined;
  /** 使用者改動時回報**正規化後的整數字串**(例 "1235")。 */
  onValueChange: (next: string) => void;
}

export function MoneyInput({ value, onValueChange, onBlur, onFocus, ...rest }: MoneyInputProps) {
  const { ref, props } = useWholeMoneyField(value, onValueChange, onBlur, onFocus);
  return <input {...rest} {...props} ref={ref} />;
}


export interface MoneyFieldProps extends Omit<MoneyInputProps, "className"> {
  /** 可見標籤。 */
  label: ReactNode;
  /** 標籤下方的說明文字。 */
  hint?: ReactNode;
}

/**
 * 有標籤的金額欄(業主 2026-10-15:全系統金額一律 0,000,000,有小數就四捨五入)。
 * 外觀與 `Input` 一樣;打字時即時加千分位,離開欄位時四捨五入成整數,往外送整數字串。
 */
export function MoneyField({ label, hint, id, ...rest }: MoneyFieldProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  return (
    <div className="flex min-w-0 flex-col gap-1">
      <label htmlFor={inputId} className="text-sm font-medium text-[var(--text-2)]">
        {label}
      </label>
      <MoneyInput
        {...rest}
        id={inputId}
        aria-describedby={hintId}
        className={
          "h-9 w-full min-w-0 rounded-[var(--radius)] border border-[var(--border-strong)] bg-[var(--surface)] px-2.5 " +
          "text-right text-sm tabular text-[var(--text)]"
        }
      />
      {hint ? (
        <span id={hintId} className="text-xs text-[var(--text-3)]">
          {hint}
        </span>
      ) : null}
    </div>
  );
}
